/**
 * Complaint Slice
 * 
 * Manages complaints state and actions
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import complaintService from '../../services/complaintService';

// Initial state
const initialState = {
  complaints: [],
  currentComplaint: null,
  statusResult: null,
  history: [],
  documents: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    status: 'all',
    priority: 'all',
    officer: '',
    searchTerm: '',
  },
  assignedComplaints: {
    data: [],
    isLoading: false,
    error: null,
    lastFetchedAt: null,
  },
};

const normalizeAuthorityAssignedComplaint = (item) => {
  if (!item || typeof item !== 'object') return null;

  const supportingDocuments = [];
  const generalDocuments = [];

  if (Array.isArray(item.files)) {
    item.files.forEach((file) => {
      if (!file) return;
      if (file.file_type === 'supporting') {
        supportingDocuments.push(file);
      } else if (file.file_type === 'general') {
        generalDocuments.push(file);
      }
    });
  }

  const userName = item.user?.name || '';
  const userEmail = item.user?.email || '';
  const userMobile = item.user?.mobile || '';
  const complaintText = item.your_complaint || '';
  const complaintStatus = item.status || item.complaint_status || '';
  const assignType = item.assign_complain_type || '';
  const deptName = item.department_name || '';
  const remarkText = item.remark || '';

  return {
    ...item,
    complaint_id: item.complaint_id || '',
    complaint_type: item.complaint_type || '',
    complaint_status: item.complaint_status || '',
    assign_complain_type: assignType,
    department_name: deptName,
    assigned_day_by_cvo: item.assigned_day_by_cvo ?? null,
    priority: item.priority || '',
    remark: remarkText,
    description: complaintText,
    complaint: complaintText,
    status: complaintStatus,
    // Map fields for EditComplaintDialog compatibility
    type: assignType,
    department: deptName,
    name: userName,
    complainant: userName, // Alias for dialog compatibility
    email: userEmail,
    mobile: userMobile,
    address: item.address || '',
    remarks: remarkText, // Alias for dialog compatibility
    supporting_documents: supportingDocuments,
    general_documents: generalDocuments,
    documents: Array.isArray(item.files) ? item.files : [],
  };
};

// Async thunks
export const fetchComplaints = createAsyncThunk(
  'complaints/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await complaintService.getComplaints(params);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCVOComplaints = createAsyncThunk(
  'complaints/fetchCVOAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await complaintService.getCVOComplaints(params);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAuthorityAssignedComplaints = createAsyncThunk(
  'complaints/fetchAuthorityAssignedComplaints',
  async (params, { rejectWithValue }) => {
    try {
      const response = await complaintService.getAuthorityAssignedComplaints(params);
      if (response.success) {
        const payload = response.data || {};
        const list = Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : [];
        return {
          list,
          meta: payload,
        };
      }
      return rejectWithValue(response.message || 'Failed to fetch assigned complaints');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchComplaint = createAsyncThunk(
  'complaints/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await complaintService.getComplaint(id);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createComplaint = createAsyncThunk(
  'complaints/create',
  async (complaintData, { rejectWithValue }) => {
    try {
      const response = await complaintService.createComplaint(complaintData);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateComplaint = createAsyncThunk(
  'complaints/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await complaintService.updateComplaint(id, data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteComplaint = createAsyncThunk(
  'complaints/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await complaintService.deleteComplaint(id);
      if (response.success) {
        return id;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchComplaintStatus = createAsyncThunk(
  'complaints/fetchStatus',
  async (data, { rejectWithValue }) => {
    try {
      const response = await complaintService.getComplaintStatus(data);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchComplaintHistory = createAsyncThunk(
  'complaints/fetchHistory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await complaintService.getComplaintHistory(id);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCVOComplaint = createAsyncThunk(
  'complaints/fetchCVOOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await complaintService.getCVOComplaint(id);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const uploadComplaintDocument = createAsyncThunk(
  'complaints/uploadDocument',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await complaintService.uploadDocument(id, formData);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Complaint slice
const complaintSlice = createSlice({
  name: 'complaints',
  initialState,
  reducers: {
    // Synchronous actions
    setCurrentComplaint: (state, action) => {
      state.currentComplaint = action.payload;
    },
    clearCurrentComplaint: (state) => {
      state.currentComplaint = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearStatusResult: (state) => {
      state.statusResult = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAuthorityAssignedComplaints: (state) => {
      state.assignedComplaints = { ...initialState.assignedComplaints };
    },
  },
  extraReducers: (builder) => {
    // Fetch complaints
    builder
      .addCase(fetchComplaints.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaints.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle different response formats
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.complaints = payload;
        } else if (payload && Array.isArray(payload.complaints)) {
          state.complaints = payload.complaints;
        } else if (payload && Array.isArray(payload.data)) {
          state.complaints = payload.data;
        } else {
          state.complaints = [];
        }
        
        if (payload && payload.pagination) {
          state.pagination = payload.pagination;
        }
      })
      .addCase(fetchComplaints.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.complaints = [];
      });

    // Fetch CVO complaints
    builder
      .addCase(fetchCVOComplaints.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCVOComplaints.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle different response formats
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.complaints = payload;
        } else if (payload && Array.isArray(payload.complaints)) {
          state.complaints = payload.complaints;
        } else if (payload && Array.isArray(payload.data)) {
          state.complaints = payload.data;
        } else {
          state.complaints = [];
        }
        
        if (payload && payload.pagination) {
          state.pagination = payload.pagination;
        }
      })
      .addCase(fetchCVOComplaints.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.complaints = [];
      });

    // Fetch authority assigned complaints
    builder
      .addCase(fetchAuthorityAssignedComplaints.pending, (state) => {
        state.assignedComplaints.isLoading = true;
        state.assignedComplaints.error = null;
      })
      .addCase(fetchAuthorityAssignedComplaints.fulfilled, (state, action) => {
        state.assignedComplaints.isLoading = false;
        const list = Array.isArray(action.payload?.list)
          ? action.payload.list
          : [];
        state.assignedComplaints.data = list
          .map(normalizeAuthorityAssignedComplaint)
          .filter(Boolean);
        state.assignedComplaints.lastFetchedAt = Date.now();
      })
      .addCase(fetchAuthorityAssignedComplaints.rejected, (state, action) => {
        state.assignedComplaints.isLoading = false;
        state.assignedComplaints.error = action.payload;
        state.assignedComplaints.data = [];
      });

    // Fetch single complaint
    builder
      .addCase(fetchComplaint.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaint.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentComplaint = action.payload;
      })
      .addCase(fetchComplaint.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Create complaint
    builder
      .addCase(createComplaint.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createComplaint.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaints.unshift(action.payload);
      })
      .addCase(createComplaint.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update complaint
    builder
      .addCase(updateComplaint.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateComplaint.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.complaints.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.complaints[index] = action.payload;
        }
        if (state.currentComplaint?.id === action.payload.id) {
          state.currentComplaint = action.payload;
        }
      })
      .addCase(updateComplaint.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Delete complaint
    builder
      .addCase(deleteComplaint.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteComplaint.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaints = state.complaints.filter(c => c.id !== action.payload);
      })
      .addCase(deleteComplaint.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch complaint status
    builder
      .addCase(fetchComplaintStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaintStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statusResult = action.payload;
      })
      .addCase(fetchComplaintStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch complaint history
    builder
      .addCase(fetchComplaintHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaintHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.history = action.payload;
      })
      .addCase(fetchComplaintHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Upload document
    builder
      .addCase(uploadComplaintDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadComplaintDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.documents.push(action.payload);
      })
      .addCase(uploadComplaintDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch single CVO complaint
    builder
      .addCase(fetchCVOComplaint.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCVOComplaint.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentComplaint = action.payload;
      })
      .addCase(fetchCVOComplaint.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const {
  setCurrentComplaint,
  clearCurrentComplaint,
  setFilters,
  clearFilters,
  clearStatusResult,
  clearError,
  clearAuthorityAssignedComplaints,
} = complaintSlice.actions;

// Export selectors
export const selectComplaints = (state) => state.complaints.complaints;
export const selectCurrentComplaint = (state) => state.complaints.currentComplaint;
export const selectStatusResult = (state) => state.complaints.statusResult;
export const selectComplaintHistory = (state) => state.complaints.history;
export const selectComplaintLoading = (state) => state.complaints.isLoading;
export const selectComplaintError = (state) => state.complaints.error;
export const selectComplaintFilters = (state) => state.complaints.filters;
export const selectComplaintPagination = (state) => state.complaints.pagination;
export const selectAuthorityAssignedComplaints = (state) => state.complaints.assignedComplaints;

// Export reducer
export default complaintSlice.reducer;

